import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
    Activity,
    ArrowRight,
    BarChart3,
    CheckCircle,
    DollarSign,
    FileText,
    Headphones,
    RefreshCw,
    Shield,
    Target,
    UserCheck,
    UserPlus,
    Users,
    Wallet,
    type LucideIcon,
} from 'lucide-react';

import httpClient, { ApiResponse } from '@/api/httpClient';
import { API_ENDPOINTS } from '@/api/endpoints.ts';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { dashboardDate, firstName, greetingPeriod } from './dashboard-greeting';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import DateTimeFormatter from '@/components/shared/DateTimeFormatter';
import { EmptyState } from '@/components/shared/EmptyState';
import {
    PageContainer,
    PageHeader,
    StatTile,
    StatTileGrid,
    type StatTileTone,
} from '@/components/shared/layout';
import { HorizontalBarChart } from '@/components/shared/charts/HorizontalBarChart';
import { DonutChart } from '@/components/shared/charts/DonutChart';
import { formatCurrency } from '@/lib/format';
import { cn } from '@/lib/utils';
import TradingMetricsWidget from '@/components/dashboard/TradingMetricsWidget';
import AutomationStatusWidget from '@/components/trading/AutomationStatusWidget';
import MarginCallWidget from '@/components/trading/MarginCallWidget';
import StopLossTakeProfitWidget from '@/components/trading/StopLossTakeProfitWidget';
import RealTimePriceWidget from '@/components/trading/RealTimePriceWidget';
import RealTimeMarketWidget from '@/components/trading/RealTimeMarketWidget';
import { TradingAnalyticsGate } from '@/components/TradingPermissionGate';

/** Re-render once a minute so the greeting rolls over (e.g. at noon). */
function useNow(intervalMs = 60_000) {
    const [now, setNow] = React.useState(() => new Date());
    React.useEffect(() => {
        const id = window.setInterval(() => setNow(new Date()), intervalMs);
        return () => window.clearInterval(id);
    }, [intervalMs]);
    return now;
}

/* ------------------------------------------------------------------ *
 * API contracts — unchanged from the previous implementation.
 * ------------------------------------------------------------------ */

interface DashboardStats {
    leads: {
        total: number;
        new: number;
        contacted: number;
        qualified: number;
        converted: number;
        conversion_rate: number;
    };
    customers: {
        total: number;
        active: number;
        new_this_month: number;
        growth_rate: number;
    };
    trading: {
        total_volume: number;
        total_trades: number;
        profitable_trades: number;
        loss_trades: number;
        win_rate: number;
        average_trade_size: number;
    };
    kyc: {
        pending: number;
        approved: number;
        rejected: number;
        completion_rate: number;
    };
    revenue: {
        total: number;
        this_month: number;
        last_month: number;
        growth_rate: number;
    };
}

interface RecentActivity {
    id: string;
    type:
        | 'lead_created'
        | 'customer_registered'
        | 'kyc_approved'
        | 'trade_executed'
        | 'withdrawal_requested';
    title: string;
    description: string;
    timestamp: string;
    user_name?: string;
    amount?: number;
}

type DashboardRole = 'admin' | 'manager' | 'agent' | 'support' | 'compliance';

function resolveDashboardRole(roleName?: string | null): DashboardRole {
    const name = (roleName ?? '').toLowerCase();
    if (name.includes('admin')) return 'admin';
    if (name.includes('manager')) return 'manager';
    if (name.includes('support')) return 'support';
    if (name.includes('compliance')) return 'compliance';
    return 'agent'; // Default for custom roles and agents
}

/* ------------------------------------------------------------------ *
 * Small shared building blocks
 * ------------------------------------------------------------------ */

const ACTIVITY_ICON: Record<RecentActivity['type'], { icon: LucideIcon; tone: string }> = {
    lead_created: { icon: UserPlus, tone: 'text-info' },
    customer_registered: { icon: Users, tone: 'text-success' },
    kyc_approved: { icon: Shield, tone: 'text-primary' },
    trade_executed: { icon: BarChart3, tone: 'text-warning' },
    withdrawal_requested: { icon: Wallet, tone: 'text-destructive' },
};

const ActivityTypeIcon: React.FC<{ type: RecentActivity['type'] }> = ({ type }) => {
    const entry = ACTIVITY_ICON[type] ?? { icon: Activity, tone: 'text-muted-foreground' };
    const Icon = entry.icon;
    return <Icon className={cn('h-4 w-4', entry.tone)} />;
};

type BadgeTone = 'success' | 'warning' | 'destructive' | 'neutral';

const BADGE_TONE_CLASS: Record<BadgeTone, string> = {
    success: 'border-transparent bg-success/10 text-success',
    warning: 'border-transparent bg-warning/10 text-warning',
    destructive: 'border-transparent bg-destructive/10 text-destructive',
    neutral: '',
};

interface PanelRow {
    label: string;
    value: React.ReactNode;
    icon?: LucideIcon;
    tone?: BadgeTone;
}

const PanelRowList: React.FC<{ rows: PanelRow[] }> = ({ rows }) => (
    <div className="space-y-2">
        {rows.map(row => (
            <div key={row.label} className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2 text-muted-foreground">
                    {row.icon && <row.icon className="h-3 w-3" />}
                    {row.label}
                </span>
                <Badge
                    variant={row.tone && row.tone !== 'neutral' ? 'outline' : 'secondary'}
                    className={row.tone ? BADGE_TONE_CLASS[row.tone] : undefined}
                >
                    {row.value}
                </Badge>
            </div>
        ))}
    </div>
);

interface DashboardPanelProps {
    icon: LucideIcon;
    title: string;
    isLoading?: boolean;
    headline?: { label: string; value: React.ReactNode };
    chart?: React.ReactNode;
    rows?: PanelRow[];
    viewAllLabel: string;
    viewAllHref: string;
}

/**
 * The one panel shape every dashboard card uses: an optional headline number,
 * an optional chart, an optional row list, and a "view all" link. Which parts
 * are supplied is what makes the Admin "Lead Conversion" card different from
 * the Agent "My Leads" card, rather than five separate hand-rolled cards.
 */
const DashboardPanel: React.FC<DashboardPanelProps> = ({
    icon: Icon,
    title,
    isLoading = false,
    headline,
    chart,
    rows,
    viewAllLabel,
    viewAllHref,
}) => (
    <Card>
        <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
                <Icon className="h-5 w-5 text-muted-foreground" />
                {title}
            </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
            {isLoading ? (
                <div className="space-y-3">
                    <Skeleton className="h-4 w-1/2" />
                    <Skeleton className="h-32 w-full" />
                </div>
            ) : (
                <>
                    {headline && (
                        <div className="flex items-center justify-between">
                            <span className="text-sm text-muted-foreground">{headline.label}</span>
                            <span className="text-lg font-semibold tabular-figures">
                                {headline.value}
                            </span>
                        </div>
                    )}
                    {chart}
                    {rows && <PanelRowList rows={rows} />}
                    <Button variant="outline" size="sm" className="w-full" asChild>
                        <Link to={viewAllHref}>
                            {viewAllLabel}
                            <ArrowRight className="h-4 w-4" />
                        </Link>
                    </Button>
                </>
            )}
        </CardContent>
    </Card>
);

/** Builds the lead-funnel panel props shared by Admin/Manager/Agent. */
function leadStagePanel(opts: {
    title: string;
    stages: { label: string; count: number }[];
    conversionRate?: number;
    viewAllLabel: string;
    viewAllHref: string;
    isLoading: boolean;
}): DashboardPanelProps {
    return {
        icon: Target,
        title: opts.title,
        isLoading: opts.isLoading,
        headline:
            opts.conversionRate !== undefined
                ? { label: 'Conversion Rate', value: `${opts.conversionRate.toFixed(1)}%` }
                : undefined,
        chart: (
            <HorizontalBarChart
                data={opts.stages.map(stage => ({ label: stage.label, value: stage.count }))}
                emptyTitle="No leads yet"
                emptyDescription="Leads will appear here once they come in."
            />
        ),
        viewAllLabel: opts.viewAllLabel,
        viewAllHref: opts.viewAllHref,
    };
}

/** Builds the KYC donut panel props shared by Admin/Support/Compliance. */
function kycStatusPanel(opts: {
    title: string;
    kyc: DashboardStats['kyc'];
    showCompletionRate?: boolean;
    viewAllLabel: string;
    viewAllHref: string;
    isLoading: boolean;
}): DashboardPanelProps {
    const total = opts.kyc.pending + opts.kyc.approved + opts.kyc.rejected;
    return {
        icon: Shield,
        title: opts.title,
        isLoading: opts.isLoading,
        headline: opts.showCompletionRate
            ? { label: 'Completion Rate', value: `${opts.kyc.completion_rate.toFixed(1)}%` }
            : undefined,
        chart: (
            <DonutChart
                data={[
                    { label: 'Pending', value: opts.kyc.pending },
                    { label: 'Approved', value: opts.kyc.approved },
                    { label: 'Rejected', value: opts.kyc.rejected },
                ]}
                centerLabel="Total"
                centerValue={total}
                emptyTitle="No KYC submissions yet"
            />
        ),
        viewAllLabel: opts.viewAllLabel,
        viewAllHref: opts.viewAllHref,
    };
}

/** Trading Performance panel (Admin only). No time series in the API — a small totals chart, not an invented history. */
function tradingPerformancePanel(
    trading: DashboardStats['trading'],
    isLoading: boolean
): DashboardPanelProps {
    return {
        icon: BarChart3,
        title: 'Trading Performance',
        isLoading,
        headline: { label: 'Win Rate', value: `${trading.win_rate.toFixed(1)}%` },
        chart: (
            <>
                <HorizontalBarChart
                    data={[
                        { label: 'Winning Trades', value: trading.profitable_trades },
                        { label: 'Losing Trades', value: trading.loss_trades },
                    ]}
                    emptyTitle="No trades yet"
                />
                <div className="flex items-center justify-between border-t border-border pt-3 text-sm">
                    <span className="text-muted-foreground">Avg Trade Size</span>
                    <span className="font-medium tabular-figures">
                        {formatCurrency(trading.average_trade_size, 'USD', { decimals: 0 })}
                    </span>
                </div>
            </>
        ),
        viewAllLabel: 'View Trading History',
        viewAllHref: '/trading-history',
    };
}

const PanelGrid: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-3">{children}</div>
);

/* ------------------------------------------------------------------ *
 * Stat tiles
 * ------------------------------------------------------------------ */

interface StatTileSpec {
    key: string;
    label: string;
    icon: LucideIcon;
    tone: StatTileTone;
    emphasizeValue?: boolean;
    value: (stats: DashboardStats) => React.ReactNode;
    hint?: (stats: DashboardStats) => React.ReactNode;
    delta?: (
        stats: DashboardStats
    ) => { value: string; direction: 'up' | 'down' | 'flat' } | undefined;
}

function growthDelta(
    rate: number | undefined | null
): { value: string; direction: 'up' | 'down' | 'flat' } | undefined {
    if (rate === undefined || rate === null || Number.isNaN(rate)) return undefined;
    return {
        value: `${rate > 0 ? '+' : ''}${rate.toFixed(1)}%`,
        direction: rate > 0 ? 'up' : rate < 0 ? 'down' : 'flat',
    };
}

/* ------------------------------------------------------------------ *
 * Quick actions
 * ------------------------------------------------------------------ */

interface QuickAction {
    label: string;
    icon: LucideIcon;
    to: string;
    /** Wraps the tile in TradingAnalyticsGate (admin's "Trading History" tile). */
    tradingGated?: boolean;
}

const QuickActionTile: React.FC<{ action: QuickAction }> = ({ action }) => {
    const tile = (
        <Link
            to={action.to}
            className="flex h-20 flex-col items-center justify-center gap-2 rounded-lg border border-border bg-card text-sm font-medium text-foreground transition-colors hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        >
            <action.icon className="h-6 w-6" />
            <span>{action.label}</span>
        </Link>
    );

    return action.tradingGated ? <TradingAnalyticsGate>{tile}</TradingAnalyticsGate> : tile;
};

const QuickActionsGrid: React.FC<{ actions: QuickAction[] }> = ({ actions }) => (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {actions.map(action => (
            <QuickActionTile key={action.to + action.label} action={action} />
        ))}
    </div>
);

/* ------------------------------------------------------------------ *
 * Recent activity (Admin only)
 * ------------------------------------------------------------------ */

const RecentActivityPanel: React.FC<{
    activity: RecentActivity[] | undefined;
    isLoading: boolean;
}> = ({ activity, isLoading }) => (
    <Card>
        <CardHeader>
            <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5" />
                Recent Activity
            </CardTitle>
        </CardHeader>
        <CardContent>
            {isLoading ? (
                <div className="space-y-3">
                    {Array.from({ length: 4 }).map((_, index) => (
                        <Skeleton key={index} className="h-12 w-full" />
                    ))}
                </div>
            ) : activity && activity.length > 0 ? (
                <div className="space-y-4">
                    {activity.slice(0, 10).map(item => (
                        <div
                            key={item.id}
                            className="flex items-start gap-3 rounded-lg p-3 transition-colors hover:bg-muted/50"
                        >
                            <div className="mt-1">
                                <ActivityTypeIcon type={item.type} />
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center justify-between gap-2">
                                    <p className="text-sm font-medium">{item.title}</p>
                                    <span className="shrink-0 text-xs text-muted-foreground">
                                        <DateTimeFormatter date={item.timestamp} />
                                    </span>
                                </div>
                                <p className="mt-1 text-sm text-muted-foreground">
                                    {item.description}
                                </p>
                                {item.amount !== undefined && (
                                    <p className="mt-1 text-sm font-medium text-success">
                                        {formatCurrency(item.amount, 'USD', { decimals: 0 })}
                                    </p>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <EmptyState icon={Activity} title="No recent activity" size="sm" />
            )}
        </CardContent>
    </Card>
);

/* ------------------------------------------------------------------ *
 * Role → dashboard content
 * ------------------------------------------------------------------ */

interface RoleDashboard {
    description: string;
    statTiles: StatTileSpec[];
    panels: (stats: DashboardStats, isLoading: boolean) => DashboardPanelProps[];
    quickActions: QuickAction[];
    /** Admin/Manager only: the existing trading widgets, unchanged. */
    renderTradingWidgets?: () => React.ReactNode;
}

const ROLE_DASHBOARDS: Record<DashboardRole, RoleDashboard> = {
    admin: {
        description: 'Complete system overview and management',
        statTiles: [
            {
                key: 'totalLeads',
                label: 'Total Leads',
                icon: UserPlus,
                tone: 'info',
                value: s => s.leads.total,
                hint: s => `${s.leads.new} new this week`,
                delta: s => ({ value: `${s.leads.conversion_rate.toFixed(1)}%`, direction: 'up' }),
            },
            {
                key: 'activeCustomers',
                label: 'Active Customers',
                icon: Users,
                tone: 'success',
                value: s => s.customers.active,
                hint: s => `${s.customers.new_this_month} new this month`,
                delta: s => growthDelta(s.customers.growth_rate),
            },
            {
                key: 'tradingVolume',
                label: 'Trading Volume',
                icon: BarChart3,
                tone: 'brand',
                value: s => formatCurrency(s.trading.total_volume, 'USD', { decimals: 0 }),
                hint: s => `${s.trading.total_trades} total trades`,
            },
            {
                key: 'monthlyRevenue',
                label: 'Monthly Revenue',
                icon: DollarSign,
                tone: 'warning',
                emphasizeValue: true,
                value: s => formatCurrency(s.revenue.this_month, 'USD', { decimals: 0 }),
                hint: () => 'This month',
                delta: s => growthDelta(s.revenue.growth_rate),
            },
        ],
        panels: (stats, isLoading) => [
            leadStagePanel({
                title: 'Lead Conversion',
                stages: [
                    { label: 'New', count: stats.leads.new },
                    { label: 'Contacted', count: stats.leads.contacted },
                    { label: 'Qualified', count: stats.leads.qualified },
                    { label: 'Converted', count: stats.leads.converted },
                ],
                conversionRate: stats.leads.conversion_rate,
                viewAllLabel: 'View All Leads',
                viewAllHref: '/leads',
                isLoading,
            }),
            tradingPerformancePanel(stats.trading, isLoading),
            kycStatusPanel({
                title: 'KYC Status',
                kyc: stats.kyc,
                showCompletionRate: true,
                viewAllLabel: 'View KYC Requests',
                viewAllHref: '/kyc-requests',
                isLoading,
            }),
        ],
        quickActions: [
            { label: 'Manage Leads', icon: UserPlus, to: '/leads' },
            { label: 'View Customers', icon: Users, to: '/customers' },
            { label: 'KYC Requests', icon: Shield, to: '/kyc-requests' },
            {
                label: 'Trading History',
                icon: BarChart3,
                to: '/trading-history',
                tradingGated: true,
            },
        ],
        renderTradingWidgets: () => (
            <>
                <TradingMetricsWidget />
                <TradingAnalyticsGate>
                    <div className="mb-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
                        <AutomationStatusWidget
                            className="bg-card border-border"
                            showActions={false}
                        />
                        <MarginCallWidget className="bg-card border-border" />
                        <StopLossTakeProfitWidget className="bg-card border-border" />
                    </div>
                </TradingAnalyticsGate>
                <TradingAnalyticsGate>
                    <div className="mb-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
                        <RealTimePriceWidget
                            className="bg-card border-border"
                            maxInstruments={5}
                            refreshInterval={5000}
                            showActions={false}
                        />
                        <RealTimeMarketWidget
                            className="bg-card border-border"
                            maxInstruments={6}
                            refreshInterval={10000}
                            showActions={false}
                        />
                    </div>
                </TradingAnalyticsGate>
            </>
        ),
    },

    manager: {
        description: 'Team performance and management overview',
        statTiles: [
            {
                key: 'teamLeads',
                label: 'Team Leads',
                icon: UserPlus,
                tone: 'info',
                value: s => s.leads.total,
                hint: s => `${s.leads.new} new this week`,
                delta: s => ({ value: `${s.leads.conversion_rate.toFixed(1)}%`, direction: 'up' }),
            },
            {
                key: 'activeCustomers',
                label: 'Active Customers',
                icon: Users,
                tone: 'success',
                value: s => s.customers.active,
                hint: s => `${s.customers.new_this_month} new this month`,
                delta: s => growthDelta(s.customers.growth_rate),
            },
            {
                key: 'teamPerformance',
                label: 'Team Performance',
                icon: BarChart3,
                tone: 'brand',
                value: s => `${s.trading.win_rate.toFixed(1)}%`,
                hint: () => 'Win rate',
            },
        ],
        panels: (stats, isLoading) => [
            // "Team Overview" (team member count / active-today) is dropped: the
            // dashboard API doesn't expose those figures anywhere, and the
            // previous implementation hard-coded them as literal 12 / 8.
            leadStagePanel({
                title: 'Lead Management',
                stages: [
                    { label: 'Assigned to Team', count: stats.leads.total },
                    { label: 'In Progress', count: stats.leads.contacted },
                    { label: 'Converted', count: stats.leads.converted },
                ],
                viewAllLabel: 'View Team Leads',
                viewAllHref: '/leads',
                isLoading,
            }),
        ],
        quickActions: [
            { label: 'Team Leads', icon: UserPlus, to: '/leads' },
            { label: 'Team Members', icon: Users, to: '/users' },
            { label: 'Customer Management', icon: UserCheck, to: '/customers' },
        ],
        renderTradingWidgets: () => <TradingMetricsWidget />,
    },

    agent: {
        description: 'Your leads and customer management',
        statTiles: [
            {
                key: 'myLeads',
                label: 'My Leads',
                icon: UserPlus,
                tone: 'info',
                value: s => s.leads.total,
                hint: s => `${s.leads.new} new this week`,
            },
            {
                key: 'myCustomers',
                label: 'My Customers',
                icon: Users,
                tone: 'success',
                value: s => s.customers.active,
                hint: () => 'Active customers',
            },
            {
                key: 'conversionRate',
                label: 'Conversion Rate',
                icon: Target,
                tone: 'brand',
                value: s => `${s.leads.conversion_rate.toFixed(1)}%`,
                hint: () => 'This month',
            },
        ],
        panels: (stats, isLoading) => [
            leadStagePanel({
                title: 'My Leads',
                stages: [
                    { label: 'New', count: stats.leads.new },
                    { label: 'In Progress', count: stats.leads.contacted },
                    { label: 'Qualified', count: stats.leads.qualified },
                    { label: 'Converted', count: stats.leads.converted },
                ],
                viewAllLabel: 'View My Leads',
                viewAllHref: '/leads',
                isLoading,
            }),
            {
                icon: Users,
                title: 'My Customers',
                isLoading,
                rows: [
                    { label: 'Total Customers', value: stats.customers.total },
                    { label: 'Active', value: stats.customers.active, tone: 'success' },
                    { label: 'New This Month', value: stats.customers.new_this_month },
                ],
                viewAllLabel: 'View My Customers',
                viewAllHref: '/customers',
            },
        ],
        quickActions: [
            { label: 'My Leads', icon: UserPlus, to: '/leads' },
            { label: 'My Customers', icon: Users, to: '/customers' },
            { label: 'KYC Requests', icon: Shield, to: '/kyc-requests' },
        ],
    },

    support: {
        description: 'Customer support and service management',
        statTiles: [
            {
                key: 'activeCustomers',
                label: 'Active Customers',
                icon: Users,
                tone: 'info',
                value: s => s.customers.active,
                hint: () => 'Total customers',
            },
            {
                key: 'pendingKyc',
                label: 'Pending KYC',
                icon: Shield,
                tone: 'warning',
                value: s => s.kyc.pending,
                hint: () => 'Requires attention',
            },
            {
                key: 'recentTransactions',
                label: 'Recent Transactions',
                icon: BarChart3,
                tone: 'success',
                value: s => s.trading.total_trades,
                hint: () => 'This week',
            },
        ],
        panels: (stats, isLoading) => [
            {
                icon: Headphones,
                title: 'Customer Support',
                isLoading,
                rows: [
                    { label: 'Active Customers', value: stats.customers.active },
                    { label: 'New This Month', value: stats.customers.new_this_month },
                ],
                viewAllLabel: 'View Customers',
                viewAllHref: '/customers',
            },
            kycStatusPanel({
                title: 'KYC Management',
                kyc: stats.kyc,
                viewAllLabel: 'Manage KYC',
                viewAllHref: '/kyc-requests',
                isLoading,
            }),
        ],
        quickActions: [
            { label: 'Customer Support', icon: Users, to: '/customers' },
            { label: 'KYC Requests', icon: Shield, to: '/kyc-requests' },
            { label: 'Trading Accounts', icon: Wallet, to: '/trading-accounts' },
        ],
    },

    compliance: {
        description: 'KYC and regulatory compliance management',
        statTiles: [
            {
                key: 'pendingKyc',
                label: 'Pending KYC',
                icon: Shield,
                tone: 'warning',
                value: s => s.kyc.pending,
                hint: () => 'Requires review',
            },
            {
                key: 'approvedKyc',
                label: 'Approved KYC',
                icon: CheckCircle,
                tone: 'success',
                value: s => s.kyc.approved,
                hint: () => 'This month',
            },
            {
                key: 'completionRate',
                label: 'Completion Rate',
                icon: Target,
                tone: 'brand',
                value: s => `${s.kyc.completion_rate.toFixed(1)}%`,
                hint: () => 'Overall',
            },
        ],
        panels: (stats, isLoading) => [
            kycStatusPanel({
                title: 'KYC Overview',
                kyc: stats.kyc,
                viewAllLabel: 'Review KYC Requests',
                viewAllHref: '/kyc-requests',
                isLoading,
            }),
            {
                icon: FileText,
                title: 'Compliance Reports',
                isLoading,
                rows: [
                    { label: 'Total Customers', value: stats.customers.total },
                    { label: 'KYC Verified', value: stats.kyc.approved, tone: 'success' },
                    {
                        label: 'Verification Rate',
                        value: `${stats.kyc.completion_rate.toFixed(1)}%`,
                    },
                ],
                viewAllLabel: 'View Customer Data',
                viewAllHref: '/customers',
            },
        ],
        quickActions: [
            { label: 'KYC Review', icon: Shield, to: '/kyc-requests' },
            { label: 'Customer Data', icon: Users, to: '/customers' },
            { label: 'Audit Log', icon: FileText, to: '/audit-log' },
        ],
    },
};

/* ------------------------------------------------------------------ *
 * Page
 * ------------------------------------------------------------------ */

const CRMDashboardPage: React.FC = () => {
    const { user } = useAuth();
    const { t, i18n } = useTranslation('crm');
    const now = useNow();
    const queryClient = useQueryClient();

    const role = resolveDashboardRole(user?.role?.name);
    const dashboard = ROLE_DASHBOARDS[role];

    const { data: statsResponse, isLoading: statsLoading } = useQuery<ApiResponse<DashboardStats>>({
        queryKey: ['crm-dashboard-stats'],
        queryFn: async () => {
            const { data } = await httpClient.get<ApiResponse<DashboardStats>>(
                `${API_ENDPOINTS.CRM.DASHBOARD}/stats`
            );
            return data;
        },
        refetchInterval: 30000, // Refresh every 30 seconds
    });

    const { data: activityResponse, isLoading: activityLoading } = useQuery<
        ApiResponse<RecentActivity[]>
    >({
        queryKey: ['crm-recent-activity'],
        queryFn: async () => {
            const { data } = await httpClient.get<ApiResponse<RecentActivity[]>>(
                `${API_ENDPOINTS.CRM.DASHBOARD}/recent-activity`
            );
            return data;
        },
        refetchInterval: 60000, // Refresh every minute
        enabled: role === 'admin', // Only the admin dashboard renders the activity feed.
    });

    const handleRefresh = () => {
        queryClient.invalidateQueries({ queryKey: ['crm-dashboard-stats'] });
        if (role === 'admin') {
            queryClient.invalidateQueries({ queryKey: ['crm-recent-activity'] });
        }
    };

    // Stats default to zeroed placeholders while loading so the tiles/panels
    // can render their skeletons instead of branching on `undefined` everywhere.
    const stats: DashboardStats = statsResponse?.data ?? {
        leads: { total: 0, new: 0, contacted: 0, qualified: 0, converted: 0, conversion_rate: 0 },
        customers: { total: 0, active: 0, new_this_month: 0, growth_rate: 0 },
        trading: {
            total_volume: 0,
            total_trades: 0,
            profitable_trades: 0,
            loss_trades: 0,
            win_rate: 0,
            average_trade_size: 0,
        },
        kyc: { pending: 0, approved: 0, rejected: 0, completion_rate: 0 },
        revenue: { total: 0, this_month: 0, last_month: 0, growth_rate: 0 },
    };

    const panels = dashboard.panels(stats, statsLoading);
    const greeting = t(`greeting.${greetingPeriod(now.getHours())}`);
    const greetingName = firstName(user?.name);

    return (
        <PageContainer>
            <PageHeader
                eyebrow={dashboardDate(now, i18n.language)}
                title={
                    greetingName
                        ? t('greeting.withName', { greeting, name: greetingName })
                        : greeting
                }
                documentTitle={t('nav.dashboard', 'Dashboard')}
                actions={
                    <Button variant="outline" size="sm" onClick={handleRefresh}>
                        <RefreshCw className="h-4 w-4" />
                        Refresh
                    </Button>
                }
            />

            <StatTileGrid>
                {dashboard.statTiles.map(tile => (
                    <StatTile
                        key={tile.key}
                        label={tile.label}
                        icon={tile.icon}
                        tone={tile.tone}
                        emphasizeValue={tile.emphasizeValue}
                        isLoading={statsLoading}
                        value={tile.value(stats)}
                        hint={tile.hint?.(stats)}
                        delta={tile.delta?.(stats)}
                    />
                ))}
            </StatTileGrid>

            <PanelGrid>
                {panels.map((panel, index) => (
                    <DashboardPanel key={`${panel.title}-${index}`} {...panel} />
                ))}
            </PanelGrid>

            {role === 'admin' && (
                <RecentActivityPanel
                    activity={activityResponse?.data}
                    isLoading={activityLoading}
                />
            )}

            <QuickActionsGrid actions={dashboard.quickActions} />

            {dashboard.renderTradingWidgets?.()}
        </PageContainer>
    );
};

export default CRMDashboardPage;
